import { useState, useRef } from "react";
import { toast } from "sonner";
import { Camera, Loader2, Check } from "lucide-react";
import api from "@/config/api";
import { useAuth } from "@/contexts/AuthContext";
import { GamifiedButton } from "@/components/GamifiedButton";
import { MAX_AVATAR_BYTES, MAX_AVATAR_LABEL } from "@/lib/constants";
import { useSeo } from "@/hooks/useSeo";

const ROLE_LABELS = { student: "Élève", parent: "Parent", teacher: "Professeur", admin: "Administrateur" };
const AGES = [{ id: "enfant", label: "Enfant" }, { id: "adolescent", label: "Ado" }, { id: "adulte", label: "Adulte" }];
const LEVELS = [{ id: "debutant", label: "Débutant" }, { id: "intermediaire", label: "Intermédiaire" }, { id: "avance", label: "Avancé" }];

export default function Profile() {
  useSeo("Profil", "Gère ton profil Quran Coach : photo, prénom, catégorie d'âge et niveau d'apprentissage.");
  const { user, refresh, setUser } = useAuth();
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    first_name: user?.first_name || "", last_name: user?.last_name || "",
    age_category: user?.age_category || "adulte", level: user?.level || "debutant",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/profile", form);
      setUser(data);
      toast.success("Profil mis à jour ✅");
    } catch { toast.error("Erreur lors de la sauvegarde."); }
    finally { setSaving(false); }
  };

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_AVATAR_BYTES) { toast.error(`Image trop volumineuse (max ${MAX_AVATAR_LABEL}).`); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/profile/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      toast.success("Photo mise à jour 📸");
    } catch { toast.error("Échec de l'envoi de la photo."); }
    finally { setUploading(false); }
  };

  const initials = `${user?.first_name?.[0] || ""}${user?.last_name?.[0] || ""}`.toUpperCase() || "U";

  return (
    <div className="space-y-8 max-w-2xl">
      <div>
        <h1 className="text-3xl font-black text-slate-800">Profil</h1>
        <p className="text-slate-500">Gère tes informations et ta photo.</p>
      </div>

      <div className="bg-white p-6 sm:p-8 rounded-3xl border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)]">
        <div className="flex flex-col sm:flex-row items-center gap-6">
          <div className="relative">
            {user?.avatar_url
              ? <img src={`${process.env.REACT_APP_BACKEND_URL}${user.avatar_url}`} alt="" className="w-24 h-24 rounded-3xl object-cover" data-testid="profile-avatar" />
              : <div className="w-24 h-24 rounded-3xl bg-secondary/15 text-secondary text-3xl font-black flex items-center justify-center" data-testid="profile-avatar">{initials}</div>}
            <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="avatar-upload-btn"
              className="absolute -bottom-2 -right-2 w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center shadow-lg hover:brightness-110">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadAvatar} data-testid="avatar-input" />
          </div>
          <div className="text-center sm:text-left">
            <div className="text-xl font-black text-slate-800">{user?.first_name} {user?.last_name}</div>
            <div className="text-sm text-slate-400">{user?.email}</div>
            <span className="inline-block mt-2 text-xs font-bold bg-primary/10 text-primary px-3 py-1 rounded-full">{ROLE_LABELS[user?.role]}</span>
          </div>
        </div>
      </div>

      <div className="bg-white p-6 sm:p-8 rounded-3xl border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] space-y-5">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2 block">Prénom</label>
            <input data-testid="profile-firstname" value={form.first_name} onChange={(e) => set("first_name", e.target.value)}
              className="w-full px-4 py-3 rounded-2xl border-2 border-slate-200 focus:border-primary outline-none font-semibold" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2 block">Nom</label>
            <input data-testid="profile-lastname" value={form.last_name} onChange={(e) => set("last_name", e.target.value)}
              className="w-full px-4 py-3 rounded-2xl border-2 border-slate-200 focus:border-primary outline-none font-semibold" />
          </div>
        </div>

        <div>
          <label className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2 block">Catégorie d'âge</label>
          <div className="grid grid-cols-3 gap-2">
            {AGES.map((a) => (
              <button key={a.id} onClick={() => set("age_category", a.id)} data-testid={`profile-age-${a.id}`}
                className={`py-2.5 rounded-2xl font-bold text-sm border-2 transition-colors ${form.age_category === a.id ? "border-secondary bg-secondary/10 text-secondary" : "border-slate-200 text-slate-500"}`}>
                {a.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2 block">Niveau</label>
          <div className="grid grid-cols-3 gap-2">
            {LEVELS.map((l) => (
              <button key={l.id} onClick={() => set("level", l.id)} data-testid={`profile-level-${l.id}`}
                className={`py-2.5 rounded-2xl font-bold text-sm border-2 transition-colors ${form.level === l.id ? "border-primary bg-primary/10 text-primary" : "border-slate-200 text-slate-500"}`}>
                {l.label}
              </button>
            ))}
          </div>
        </div>

        <GamifiedButton onClick={save} disabled={saving} className="w-full" data-testid="profile-save-btn">
          {saving ? "…" : <><Check className="w-5 h-5" /> Enregistrer</>}
        </GamifiedButton>
      </div>
    </div>
  );
}
